import { useEffect, useState, useRef, useMemo, use } from "react";
import { getSetting } from "@woocommerce/settings";
import { ValidationInputError } from "@woocommerce/blocks-components";
import { controls, select, useDispatch } from "@wordpress/data";
import DynamicField from "../fieldsHtml/DynamicField";
import { useSelect } from '@wordpress/data';

import {
    getTargetConditionsToHideShowFields,
    getUpdatedFieldsWithHideShowAttribute,
    getDefaultFieldsName,
    triggerTotalsRecalc,
    setFieldValuesInLocalStorage,
    getCartCheckoutConditionValues
} from "../helper/helper-fronted";

import {
    showRequiredFieldsError,
    setBulkFieldsValuesInSession,
    getCheckboxLabelText,
    setInitialFieldData,
    saveDefaultFieldValuesInSession,
    hasUserMetaPrefillValues,
    mergeUserMetaIntoFieldValues,
    resolveUserMetaFieldStateValue,
    applyUserMetaPrefillToFieldState,
    getRepeatCount,
    isValidEmail,
    checkProductConditionsForField,
    isInlineValidationEnabled,
    validateFieldFormatRules,
    getSessionDynamicPriceFields,
    buildHiddenFieldsFeeData,
    applyRestoredFieldFees
} from "../helper/input-validations";

const AdditionalFields = ({ checkoutExtensionData }) => {

    const store = select("wc/store/cart");
    const checkout = select("wc/store/checkout");
    const cartDispatch = wp.data.dispatch('wc/store/cart');
    const checkoutDispatch = wp.data.dispatch('wc/store/checkout');
    const { applyExtensionCartUpdate } = useDispatch('wc/store/cart');
    const additionalFieldsSettings = getSetting("fme-ccfw-order-note_data");
    const { extensionCartUpdate } = wc.blocksCheckout;
    let [fields, setFields] = useState([]);
    const [fieldValues, setFieldValues] = useState({});
    const [debouncedFieldValues, setDebouncedFieldValues] = useState({});
    const [defaultFieldsTrigger, setDefaultFieldsTrigger] = useState(0);
    const [errors, setErrors] = useState({});
    const debounceTimers = useRef({});
    const lastSentValues = useRef({});
    const debounceHiddenFieldsFee = useRef({});
    const hiddenFeesHashRef = useRef('');
    const fieldValuesDebounceTimer = useRef(null);
    const allFieldsCombinedDebounceTimer = useRef(null);
    const cart = useSelect(
        (select) => select('wc/store/cart').getCartData(),
        []
    );

    const [hiddenFields, setHiddenFields] = useState({});
    const [allFieldsCombined, setAllFieldsCombined] = useState({});
    const [debouncedAllFieldsCombined, setDebouncedAllFieldsCombined] = useState({});
    const [localStorageTick, setLocalStorageTick] = useState(0);
    const { setExtensionData } = checkoutExtensionData;
    const hiddenFieldsRef = useRef({});
    const isFeeUpdatedForHidden = useRef({});
    const defaultFieldsName = getDefaultFieldsName();
    let allFields = fme_all_fields_data;
    const { dispatch } = wp.data;
    let currentUserRoles = fme_upload_data.user_roles || [];
    let customFields = [];
    let fieldsNewValues = fieldValues;
    let repeatField = [];
    const prevSnapshot = useRef(null);
    const cartTotal = cart.totals.total_price || 0; // Get cart total
    const cartSubtotal = cart.totals.total_items || 0; // Get cart subtotal
    const cartItemCount = (cart?.items || []).reduce((total, item) => total + (item.quantity || 0), 0);
    const billingAddress = cart.billingAddress || {};
    const shippingAddress = cart.shippingAddress || {};
    const useShippingAsBilling = checkout.getUseShippingAsBilling();

    // Get active payment method
    const paymentMethod = useSelect(
        (select) =>
            select('wc/store/payment')
                ?.getActivePaymentMethod?.(),
        []
    );

    // Get selected shipping method
    let shippingRate = '';
    const selectedShippingRates = useSelect(
        (select) =>
            select('wc/store/cart')
                ?.getShippingRates?.(),
        []
    );
    // Loop through shipping rates and get selected one
    if (selectedShippingRates && Array.isArray(selectedShippingRates)) {
        selectedShippingRates.forEach((shipment, shipmentIndex) => {

            if (shipment.shipping_rates && Array.isArray(shipment.shipping_rates)) {
                shipment.shipping_rates.forEach((rate, rateIndex) => {
                    if (rate.selected) {
                        shippingRate = rate.rate_id;
                        shippingRate = shippingRate.replace(/:/g, '-'); // Replace : with - for consistency

                        return false; // Break loop once selected rate is found
                    }
                });
            }
        });
    }

    // On mount, filter and set custom fields - user role based filtering - product/category based filtering - condition based filtering
    useEffect(() => {

        customFields = additionalFieldsSettings.filter((field) => {
            const userRoles = field.user_role || [];
            const specificRoles = field.specific_user_role || [];
            const cartItems = store.getCartData().items;

            let roleCheckPassed = true;
            let productCategoryCheckPassed = true;
            let condtionCheckPassed = true;

            // Skip if not enabled or in default mode
            if (field.field_mode === "default" || field.is_enable !== "1") {
                return false;
            }

            if ('checkbox' === field.field_type && '' == field.field_placeholder) {
                field.field_placeholder = 'yes';
            }

            // === User Role Check ===
            if (field.userrole_check === "fme_display_user_role") {
                if (specificRoles.length > 0) {
                    roleCheckPassed = userRoles.some((role) => specificRoles.includes(role));
                }
            } else if (field.userrole_check === "fme_hide_user_role") {
                if (specificRoles.length > 0) {
                    roleCheckPassed = !userRoles.some((role) => specificRoles.includes(role));
                }
            }

            // === Product/Category/Tag Check ===
            productCategoryCheckPassed = checkProductConditionsForField(field);

            return (
                roleCheckPassed && productCategoryCheckPassed && condtionCheckPassed
            );
        });

        // Initialize field values and errors
        const initialData = setInitialFieldData(customFields, fieldValues, setFieldValues, cart);
        const initialValues = mergeUserMetaIntoFieldValues(customFields, initialData.initialValues);
        const initialErrors = initialData.initialErrors;
        const feeData = initialData.feeData;
        repeatField = initialData.repeatField;

        setFields(customFields);
        setFieldValues(initialValues);
        setErrors(initialErrors);

        // Sync prefilled/default field fees to session (including user-meta values).
        const feeDispatch = { applyExtensionCartUpdate };
        const feeSyncDelay = hasUserMetaPrefillValues(customFields) ? 300 : 150;
        saveDefaultFieldValuesInSession(feeData, feeDispatch, feeSyncDelay);

    }, [allFields]);


    // set cart total / subtotal / shipping value / payment method in initialData for conditions check on load
    useEffect(() => {
        setFieldValues((prevValues) => ({
            ...prevValues,
            fme_ccfw_cart_total: cartTotal || 0,
            fme_ccfw_cart_subtotal: cartSubtotal || 0,
            fme_ccfw_cart_item_count: cartItemCount || 0,
            fme_ccfw_shipping_method: shippingRate || 0,
            fme_ccfw_payment_method: paymentMethod || '',
            useShippingAsBilling: true == true == useShippingAsBilling ? 'yes' : 'no',

            // ✅ ADD DEFAULT FIELDS HERE
            // Billing Address
            billing_first_name: billingAddress?.first_name || '',
            billing_last_name: billingAddress?.last_name || '',
            billing_country: billingAddress?.country || '',
            billing_state: billingAddress?.state || '',
            billing_city: billingAddress?.city || '',
            billing_postcode: billingAddress?.postcode || '',
            billing_email: billingAddress?.email || '',
            billing_phone: billingAddress?.phone || '',
            billing_address_1: billingAddress?.address_1 || '',
            billing_address_2: billingAddress?.address_2 || '',
            billing_company: billingAddress?.company || '',
            // Shipping Address
            shipping_first_name: shippingAddress?.first_name || '',
            shipping_last_name: shippingAddress?.last_name || '',
            shipping_country: shippingAddress?.country || '',
            shipping_state: shippingAddress?.state || '',
            shipping_city: shippingAddress?.city || '',
            shipping_postcode: shippingAddress?.postcode || '',
            shipping_address_1: shippingAddress?.address_1 || '',
            shipping_address_2: shippingAddress?.address_2 || '',
            shipping_phone: shippingAddress?.phone || '',
            shipping_company: shippingAddress?.company || '',
            shipping_email: shippingAddress?.email || '',

        }));


    }, [cartSubtotal, cartTotal, cartItemCount, shippingRate, paymentMethod, shippingAddress, billingAddress, useShippingAsBilling]);

    useEffect(() => {
        if (fieldValuesDebounceTimer.current) {
            clearTimeout(fieldValuesDebounceTimer.current);
        }
        fieldValuesDebounceTimer.current = setTimeout(() => {
            setDebouncedFieldValues(fieldValues);
        }, 100);
        return () => {
            if (fieldValuesDebounceTimer.current) {
                clearTimeout(fieldValuesDebounceTimer.current);
            }
        };
    }, [fieldValues]);

    // Debounce allFieldsCombined for expensive operations
    useEffect(() => {
        if (allFieldsCombinedDebounceTimer.current) {
            clearTimeout(allFieldsCombinedDebounceTimer.current);
        }
        allFieldsCombinedDebounceTimer.current = setTimeout(() => {
            setDebouncedAllFieldsCombined(allFieldsCombined);
        }, 50);
        return () => {
            if (allFieldsCombinedDebounceTimer.current) {
                clearTimeout(allFieldsCombinedDebounceTimer.current);
            }
        };
    }, [allFieldsCombined]);

    const isRepeatPriceAllowed = (field) => {
        if (field?.repeat_rules?.fme_is_repeat !== 'on') return true;
        if (field?.repeat_rules?.fme_repeat_price === 'on') return true;
        const match = String(field.field_name || '').match(/_(\d+)$/);
        return !match || Number(match[1]) === 1;
    };

    const queueSessionUpdate = ({ id, value, field, label, fees }) => {
        if (debounceTimers.current[id]) {
            clearTimeout(debounceTimers.current[id]);
        }

        debounceTimers.current[id] = setTimeout(() => {
            if (hiddenFieldsRef.current[id]) return;

            const comparisonValue = Array.isArray(value) ? JSON.stringify(value) : value;
            const prev = lastSentValues.current[id];
            if (prev === comparisonValue) return;

            lastSentValues.current[id] = comparisonValue;

            let sessionFees = fees || 0;
            if ((field?.field_type === 'multiselect' || field?.field_type === 'checkbox_group') && Array.isArray(value)) {
                sessionFees = value.reduce((sum, item) => sum + Number(item?.value || 0), 0);
            }

            const fieldFeesData = {
                fieldName: label || field?.field_label || '',
                fieldValue: value,
                fieldFees: sessionFees,
                priceType: field?.price_type || 'fixed',
                ...getSessionDynamicPriceFields(field),
                fieldType: field?.field_type,
                fieldTax: field?.is_taxable || '0'
            };


            applyExtensionCartUpdate({
                namespace: 'fme-ccfw-fee-update',
                data: {
                    trigger: 'fee_updated',
                    fieldId: id,
                    fieldValue: fieldFeesData,
                    bulkSingleUpdate: 'single' // Flag to indicate this is a single field update, not a bulk update
                }
            });

        }, 500);
    };

    const handleBlur = (field, currentValue = null) => {
        if (!isInlineValidationEnabled()) {
            return;
        }
        const id = field?.field_name;
        if (!id) return;
        const value = currentValue !== null ? currentValue : fieldValues[id];
        validateField(field, value);
    };

    // Handle field value changes
    const handleChange = (id, value, changedField = {}, label = '', fees = 0) => {

        // Get the related field config
        const baseFieldName = fields.find(f => id.startsWith(f.field_name))?.field_name;
        const hasChangedField = changedField && Object.keys(changedField).length > 0;
        const field = hasChangedField ? changedField : fields.find((f) => f.field_name === baseFieldName) || {};

        let isValid = true;
        const sessionLabel = label || field.field_label || '';

        if (field.field_type === "file") {

            if (value && typeof value === 'object') {

                const fileExtension = value.name?.split('.').pop().toLowerCase() || '';
                const fileSize = value.size;

                const allowedExtensions = field.field_extensions?.trim()
                    ? field.field_extensions.split(',').map(ext => ext.trim().toLowerCase())
                    : [];

                const maxSize = field.file_size_limit
                    ? parseFloat(field.file_size_limit)
                    : null;

                const sizeUnit = field.file_size_type || 'MB';
                const extensionValid = allowedExtensions.length === 0 || allowedExtensions.includes(fileExtension);
                const sizeValid = !maxSize || (
                    sizeUnit === 'KB'
                        ? fileSize / 1000 <= maxSize
                        : fileSize / 1000000 <= maxSize
                );

                isValid = extensionValid && sizeValid;

                if (!extensionValid) {
                    alert(`Invalid file type. Allowed: ${allowedExtensions.join(', ')}`);
                    return;
                } else if (!sizeValid) {
                    alert(`File must be ≤ ${maxSize}${sizeUnit}`);
                    return;
                }

                // ✅ If valid, proceed with upload
                const formData = new FormData();

                formData.append('fme' + field.field_name, value);
                formData.append('action', 'fme_upload_pic');
                formData.append('id', 'fme' + field.field_name);
                formData.append('ext', fileExtension);
                formData.append('security', fme_upload_data.upload_nonce);

                const queryString = `?action=fme_upload_pic&id=fme${field.field_name}&ext=${fileExtension}&security=${fme_upload_data.upload_nonce}`;

                jQuery.ajax({
                    url: fme_upload_data.ajax_url + queryString,
                    type: 'POST',
                    data: formData, // FormData includes the file
                    cache: false,
                    contentType: false,
                    processData: false,
                    success: function (response) {
                    },
                    error: function (xhr, status, error) {
                        console.error('Upload error:', error);
                        console.log('Response:', xhr.responseText);
                    }
                });

                const filename = value.name.replace(/\s+/g, '_');

                // Update field value
                setFieldValues((prevValues) => ({
                    ...prevValues,
                    [id]: filename,
                    [id + '_data']: changedField,
                }));

                value = filename;
            } else if (value === null) {
                // Update field value
                setFieldValues((prevValues) => ({
                ...prevValues,
                [id]: '',
                }));

            }

        } else if (field.field_type === "time") {

            // For Time Fields: Change time format to 12-hour with AM/PM
            // Format time to HH:MM
            const timeParts = value.split(':');
            if (timeParts.length === 2) {
                let hours = parseInt(timeParts[0], 10);
                const minutes = timeParts[1].padStart(2, '0');
                const period = hours >= 12 ? 'PM' : 'AM';
                hours = hours % 12 || 12;
                let value1 = `${String(hours).padStart(2, '0')}:${minutes} ${period}`;
            }

            setFieldValues((prevValues) => ({
                ...prevValues,
                [id]: value,
                [id + '_data']: changedField,
            }));

        } else {

            if (('multiselect' == field.field_type || 'checkbox_group' == field.field_type) && (Array.isArray(value) && value.length === 0)) {
                value = '';
            }

            setFieldValues((prevValues) => ({
                ...prevValues,
                [id]: value,
                [id + '_data']: changedField,
            }));

        }

        if (field?.is_required == 1) {

            // For checkboxes: validate immediately on change
            if (field.field_type === "checkbox") {
                validateField(field, value);
            } else if (field.field_type === "select") {
                validateField(field, value);
            } else if (field.field_type === "file") {
                validateField(field, value);
            } else {

                // For others: validate here, or let blur handle it
                if (typeof value === "string") {
                    isValid = value.trim().length > 0;
                } else if (typeof value === "number") {
                    isValid = !isNaN(value);
                } else {
                    isValid = value !== null && value !== undefined;
                }

                setErrors((prevErrors) => ({
                    ...prevErrors,
                    [id]: !isValid,
                }));

            }

        }

        queueSessionUpdate({ id, value, field, label: sessionLabel, fees });

    };

    // Validate a field
    const validateField = (field, overrideValue = null) => {

        const fieldName = field.field_name;
        const value = overrideValue !== null ? overrideValue : fieldValues[fieldName];
        let isValid = true;

        if (field?.isHide === true || fieldValues[fieldName + '_data']?.isHide === true || fieldValues[fieldName] === '---') {
            setErrors((prevErrors) => ({
                ...prevErrors,
                [fieldName]: false,
            }));
            return true;
        }

        if (field?.is_required == 1) {
            switch (field.field_type) {
                case "checkbox":
                    isValid = !!value;
                    break;

                case "file":
                    // Assuming file is valid if a file object or file name exists
                    isValid = value && (value instanceof File || typeof value === "string" && value.trim().length > 0);
                    break;

                case "select":
                case "multiselect":
                case "checkbox_group":
                    isValid = value !== null && value !== "" && value !== undefined && !(Array.isArray(value) && value.length === 0);
                    break;

                        case "email":
                            // Custom/confirm/built-in validators own format checks via validateFieldFormatRules.
                            isValid = !!value && (
                                field?.custom_validator_id || field?.confirm_validator_id || field?.builtin_validator_id
                                    ? true
                                    : isValidEmail(value)
                            );
                            break;

                case "hidden":
                case "label":
                case "heading":
                case "paragraph":
                    isValid = true;
                    break;

                default:
                    if (typeof value === "string") {
                        isValid = value.trim().length > 0;
                    } else if (typeof value === "number") {
                        isValid = !isNaN(value);
                    } else {
                        isValid = value !== null && value !== undefined;
                    }
            }
        }

        if (isValid && value !== null && value !== undefined && String(value).trim() !== '') {
            const formatResult = validateFieldFormatRules(value, field, fieldValues);
            if (!formatResult.valid) {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    [field.field_name]: true,
                    [`${field.field_name}_error_label`]: formatResult.message,
                }));
                triggerTotalsRecalc({ value });
                return false;
            }
        }

        setErrors((prevErrors) => ({
            ...prevErrors,
            [field.field_name]: !isValid,
            [`${field.field_name}_error_label`]: !isValid ? (prevErrors[`${field.field_name}_error_label`] || 'This field is required.') : undefined,
        }));

        triggerTotalsRecalc({ value });

        return isValid;

    };

    // Move the additional fields block after order notes, only once when block loads
    useEffect(() => {

        const $additionalFields = jQuery('.ccfw-additional-fields');
        const $target = jQuery('.wc-block-checkout__order-notes.wp-block-woocommerce-checkout-order-note-block');

        if ($additionalFields.length && $target.length) {
            $additionalFields.insertAfter($target);
        }

    }, []); // Empty dependency array ensures this runs only once on mount


    // Set extension data whenever fieldValues change
    useEffect(() => {

        let encodedFieldValues = setFieldValuesInLocalStorage(debouncedFieldValues, 'order_note');
        // Immediately update allFieldsCombined with current block values to avoid timing issues
        setLocalStorageTick((prev) => prev + 1);

    }, [debouncedFieldValues]);


    // Access billing fields from shipping block
    useEffect(() => {
        const handleStorageUpdate = () => {
            setLocalStorageTick((prev) => prev + 1);
        };

        window.addEventListener('storage', handleStorageUpdate);
        window.addEventListener('fme_ccfw_storage_update', handleStorageUpdate);

        return () => {
            window.removeEventListener('storage', handleStorageUpdate);
            window.removeEventListener('fme_ccfw_storage_update', handleStorageUpdate);
        };
    }, []);

    useEffect(() => {
        setLocalStorageTick((prev) => prev + 1);
    }, [cartSubtotal, cartTotal, shippingRate, paymentMethod]);

    useEffect(() => {

        const billingFieldsStr = localStorage.getItem('fme_billing_fields');
        const shippingFieldsStr = localStorage.getItem('fme_shipping_fields');
        const orderNoteFieldsStr = localStorage.getItem('fme_order_note_fields');

        const billingFields = billingFieldsStr ? JSON.parse(billingFieldsStr) : {};
        const shippingFields = shippingFieldsStr ? JSON.parse(shippingFieldsStr) : {};
        const orderNoteFields = orderNoteFieldsStr ? JSON.parse(orderNoteFieldsStr) : {};

        const combinedFields = {
            ...billingFields,
            ...shippingFields,
            ...orderNoteFields
        };

        setAllFieldsCombined(combinedFields);

    }, [localStorageTick]);


    const combinedFieldValuesForConditions = useMemo(() => ({
        ...getCartCheckoutConditionValues(cart, useShippingAsBilling),
        ...debouncedAllFieldsCombined,
        ...debouncedFieldValues,
    }), [debouncedAllFieldsCombined, debouncedFieldValues, cart, useShippingAsBilling]);

    const targetConditionsMap = useMemo(() => {
        return getTargetConditionsToHideShowFields(fme_all_fields_data, combinedFieldValuesForConditions);

    }, [combinedFieldValuesForConditions, defaultFieldsTrigger]);


    const processedData = useMemo(() => {
        return getUpdatedFieldsWithHideShowAttribute(
            targetConditionsMap,
            fields,
            allFields,
            combinedFieldValuesForConditions
        );
    }, [targetConditionsMap, fields, allFields, combinedFieldValuesForConditions, defaultFieldsTrigger]);

    fields = processedData.fields;
    allFields = processedData.allFields;
    fieldsNewValues = processedData.fieldValues;

    const visibleFieldStateKey = useMemo(
        () => fields.map((field) => `${field.field_name}:${field.isHide ? 1 : 0}`).join('|'),
        [fields]
    );

    useEffect(() => {
        if (!fields.length) {
            return;
        }
        setFieldValues((prev) => applyUserMetaPrefillToFieldState(fields, prev));
    }, [visibleFieldStateKey, billingAddress?.first_name]);

    useEffect(() => {
        const t = setTimeout(() => {
            showRequiredFieldsError(debouncedFieldValues, errors, setErrors, processedData.fields);
        }, 200);
        return () => clearTimeout(t);
    }, [debouncedFieldValues, processedData.fields]);

    useEffect(() => {
        const valueUpdates = {};
        const errorUpdates = {};
        const restoredFieldsForFees = [];

        processedData.fields.forEach((field) => {
            const fieldName = field.field_name;
            if (field.isHide) {
                if (fieldValues[fieldName] !== '---') {
                    const current = fieldValues[fieldName];
                    if (current !== undefined && current !== '' && current !== '---') {
                        valueUpdates[fieldName + '_fme_prefill'] = current;
                    }
                    valueUpdates[fieldName] = '---';
                    valueUpdates[fieldName + '_data'] = { ...(fieldValues[fieldName + '_data'] || field), isHide: true, field_name: fieldName };
                }
                hiddenFieldsRef.current[fieldName] = true;
                lastSentValues.current[fieldName] = '';
                errorUpdates[fieldName] = false;
            } else if (fieldValues[fieldName] === '---') {
                const meta = resolveUserMetaFieldStateValue(field, fieldName, field.field_label);
                const restoredValue = fieldValues[fieldName + '_fme_prefill'] ?? (meta ? meta.value : '');
                valueUpdates[fieldName] = restoredValue;
                valueUpdates[fieldName + '_data'] = {
                    ...(meta?.data || fieldValues[fieldName + '_data'] || field),
                    isHide: false,
                    field_name: fieldName,
                };
                hiddenFieldsRef.current[fieldName] = false;
                restoredFieldsForFees.push({
                    fieldName,
                    value: restoredValue,
                    field,
                    fieldLabel: field.field_label,
                });
                errorUpdates[fieldName] = false;
            }
        });

        if (Object.keys(valueUpdates).length > 0) {
            setFieldValues((prevValues) => ({
                ...prevValues,
                ...valueUpdates,
            }));
        }

        if (Object.keys(errorUpdates).length > 0) {
            setErrors((prevErrors) => ({
                ...prevErrors,
                ...errorUpdates,
            }));
        }

        if (restoredFieldsForFees.length > 0) {
            const feeDispatch = { applyExtensionCartUpdate };
            setTimeout(() => {
                applyRestoredFieldFees(restoredFieldsForFees, feeDispatch, lastSentValues);
            }, 50);
        }
    }, [processedData.fields]);

    // Set extension data with processed field values (after conditional logic)
    useEffect(() => {
        // Filter processedData.fieldValues to exclude billing and shipping fields
        const filteredFieldValues = Object.fromEntries(
            Object.entries(processedData.fieldValues || {}).filter(([key]) =>
                !key.startsWith('billing') && !key.startsWith('shipping')
            )
        );

        // Prepare a copy of filtered field values, JSON-encoding any array values
        const encodedFieldValues = Object.fromEntries(
            Object.entries(filteredFieldValues).map(([key, val]) => [
                key,
                Array.isArray(val) ? JSON.stringify(val) : val,
            ])
        );

        // Filter allFields to exclude billing and shipping fields
        const filteredAllFields = Array.isArray(processedData.allFields)
            ? processedData.allFields.filter(field =>
                !field.field_name?.startsWith('billing_') && !field.field_name?.startsWith('shipping_')
            )
            : [];

        // Encode filtered allFields
        const encodedAllFields = filteredAllFields.map(field =>
            Object.fromEntries(
                Object.entries(field).map(([key, val]) => [
                    key,
                    Array.isArray(val) ? JSON.stringify(val) : val,
                ])
            )
        );

        setExtensionData("fme-ccfw-order-note", "additional_fields", {
            additionalFields: encodedFieldValues,
            allFields: encodedAllFields
        });

    }, [processedData.fieldValues, processedData.allFields, setExtensionData]);

    // Unset hidden fields values in session - to prevent / remove their fees from cart total
    useEffect(() => {
        const feeData = buildHiddenFieldsFeeData({
            fieldsNewValues,
            hiddenFieldsRef,
            lastSentValues,
        });

        // Always check if we need to update fees (even if feeData is empty)
        const hash = JSON.stringify(feeData);
        if (hiddenFeesHashRef.current === hash) {
            return;
        }

        hiddenFeesHashRef.current = hash;

        if (debounceHiddenFieldsFee.current.timer) {
            clearTimeout(debounceHiddenFieldsFee.current.timer);
        }

        debounceHiddenFieldsFee.current.timer = setTimeout(() => {
            // Trigger cart update to recalculate totals based on hidden fields, even if no specific fee data (to handle cases where hiding a field should remove fees)
            applyExtensionCartUpdate({
                namespace: 'fme-ccfw-fee-update',
                data: {
                    trigger: 'hidden_fields_updated',
                    feeData: feeData,
                    bulkSingleUpdate: 'bulk' // Flag to indicate this is a bulk update for multiple fields
                }
            });

        }, 700);

        return () => {
            if (debounceHiddenFieldsFee.current.timer) {
                clearTimeout(debounceHiddenFieldsFee.current.timer);
            }
        };
    }, [JSON.stringify(fieldsNewValues), JSON.stringify(fieldValues), fields, cart]);

    // Render the additional fields
    return (
        <div className="ccfw-additional-fields" id="ccfw-additional-fields">
            {fields.map((field) => {

                if (hiddenFields[field.field_name]) {
                    return null;
                }

                const nonRepeatedfieldName = field.field_name ?? '';
                const shouldRepeat = field.repeat_rules.fme_is_repeat == 'on';
                let repeatCount = shouldRepeat ? getRepeatCount(cart, field.repeat_rules) : 1;
                if (0 == repeatCount) {
                    repeatCount = 1;
                }

                return (
                    <React.Fragment key={field.field_name}>
                        {[...Array(repeatCount)].map((_, index) => {

                            const isOriginal = index === 0;
                            const fieldIndex = index + 1;
                            const suffix = field.repeat_rules?.fme_repeat_label_suffix === 'alphabet'
                                ? String.fromCharCode(65 + index)
                                : (fieldIndex).toString();

                            const fieldName = isOriginal ? field.field_name : `${field.field_name}_${fieldIndex}`;
                            const fieldLabel = isOriginal ? field.field_label : `${field.field_label} (${suffix})`;
                            // let fieldValue = fieldValues[fieldName] || '';
                            let fieldValue =
                                fieldValues[fieldName] !== undefined
                                    ? fieldValues[fieldName]
                                    : '';
                            const error = errors[fieldName];

                            let isRepeatPrice = field.repeat_rules.fme_is_repeat == 'on' && field.repeat_rules.fme_repeat_price != 'on';
                            let repeatPrice = !isOriginal && isRepeatPrice ? false : true;

                            // Skip rendering if field is set to be hidden
                            if (true == field.isHide) {
                                return null;
                            }

                            return (
                                <div
                                    key={fieldName}
                                    className={`${field.field_name} ${field.field_name}_field_container ${field.sort_order} ccfw-field-container margin-bottom-10`}
                                >
                                    <DynamicField
                                        field={{
                                            ...field,
                                            field_name: fieldName,
                                            field_label: fieldLabel,
                                        }}
                                        fieldValue={fieldValue}
                                        error={error}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        cart={cart}
                                        repeatPrice={repeatPrice}
                                        allFieldValues={{ ...allFieldsCombined, ...fieldValues }}
                                    />
                                    {error && (
                                        <ValidationInputError
                                            errorMessage={errors[`${fieldName}_error_label`] || 'This field is required.'}
                                            id={fieldName}
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </React.Fragment>
                );
            })}
        </div>
    );
};

export const Block = ({ extensions, checkoutExtensionData }) => {
    return (
        <>
            <AdditionalFields checkoutExtensionData={checkoutExtensionData} />
        </>
    );
};